import React, { useRef, useState } from "react";
import { ImagePlus, Upload, X, Plus } from "lucide-react";
import { fileToBase64, MAX_IMAGE_MB, MAX_GALLERY_IMAGES } from "../utils/Utils";

/**
 * Galería de imágenes: varias a la vez, por archivo o por URL.
 *
 * Igual que ImageField, los archivos se convierten a base64 aquí y viajan en
 * el JSON del formulario; es el backend quien los sube a imgbb (utils/images.js).
 * Por eso cada elemento de `value` puede ser un data URI recién elegido o una
 * URL ya guardada.
 */
function GalleryField({
  label = "Galería (opcional)",
  value = [],
  onChange,
  max = MAX_GALLERY_IMAGES,
  hint = "Añade varias fotos del proyecto. Se mostrarán en su página.",
}) {
  const inputRef = useRef(null);
  const [url, setUrl] = useState("");

  const left = max - value.length;

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    // Se limpia el input para que elegir los mismos archivos dos veces dispare el change
    e.target.value = "";
    if (files.length === 0) return;

    if (files.length > left) {
      alert(`Solo caben ${max} imágenes en la galería; se añadirán las ${left} primeras.`);
    }

    const added = [];
    for (const file of files.slice(0, left)) {
      if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
        alert(`"${file.name}" pesa más de ${MAX_IMAGE_MB} MB y no se ha añadido.`);
        continue;
      }
      try {
        added.push(await fileToBase64(file));
      } catch (error) {
        console.error("Error al leer la imagen:", error);
        alert(`No se ha podido leer "${file.name}".`);
      }
    }

    if (added.length > 0) onChange([...value, ...added]);
  };

  const addUrl = () => {
    const clean = url.trim();
    if (!clean || left <= 0) return;
    onChange([...value, clean]);
    setUrl("");
  };

  const removeAt = (index) => onChange(value.filter((_, i) => i !== index));

  return (
    <div className="dyd-field">
      <label className="dyd-label">
        {label}
        {value.length > 0 && <span className="dyd-hint"> · {value.length}/{max}</span>}
      </label>

      {value.length > 0 && (
        <div className="dyd-gallery-field">
          {value.map((image, index) => (
            <div key={`${image.slice(0, 32)}-${index}`} className="dyd-gallery-thumb">
              <img src={image} alt={`Imagen ${index + 1}`} />
              <button
                type="button"
                className="dyd-gallery-remove"
                onClick={() => removeAt(index)}
                title="Quitar imagen"
                aria-label={`Quitar imagen ${index + 1}`}
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {value.length === 0 && (
        <div className="dyd-gallery-empty">
          <ImagePlus size={22} strokeWidth={1.5} />
          <span className="dyd-hint">Todavía no has añadido ninguna imagen</span>
        </div>
      )}

      <div className="dyd-gallery-actions">
        <button
          type="button"
          className="dyd-btn dyd-btn-ghost dyd-btn-sm"
          onClick={() => inputRef.current?.click()}
          disabled={left <= 0}
        >
          <Upload size={15} />
          Subir imágenes
        </button>

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: "none" }}
          onChange={handleFiles}
        />
      </div>

      {left > 0 && (
        <div className="dyd-gallery-url">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addUrl();
              }
            }}
            placeholder="…o pega una URL"
            className="dyd-input"
          />
          <button type="button" className="dyd-btn dyd-btn-subtle dyd-btn-sm" onClick={addUrl}>
            <Plus size={15} /> Añadir
          </button>
        </div>
      )}

      <small className="dyd-hint">
        {left > 0 ? hint : `Has llegado al máximo de ${max} imágenes.`}
      </small>
    </div>
  );
}

export default GalleryField;
